/* eslint-disable no-plusplus, react/no-array-index-key, jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
import React from 'react';
import PropTypes from 'prop-types';
import { DEPTH_COLORS } from '../../constants/treeConstants';

const JSONNode = ({ keyName, value, depth, collapsed, onToggle, classes, lineRef, isLast }) => {
  const id = `${depth}-${keyName}`;
  const isCollapsed = collapsed[id];
  const isArray = Array.isArray(value);
  const isObject = value !== null && typeof value === 'object';
  const bgColor = DEPTH_COLORS[depth % DEPTH_COLORS.length];
  const comma = isLast ? '' : ',';

  if (!isObject) {
    lineRef.current++;

    const renderValue = () => {
      if (value === null || value === undefined) {
        return <span className={classes.jsonNull}>{String(value)}</span>;
      }
      if (typeof value === 'boolean') {
        return <span className={classes.attrName}>{String(value)}</span>;
      }
      if (typeof value === 'number') {
        return <span className={classes.jsonNumber}>{value}</span>;
      }
      return (
        <span>
          <span className={classes.tagBracket}>&quot;</span>
          <span className={classes.attrValue}>{value}</span>
          <span className={classes.tagBracket}>&quot;</span>
        </span>
      );
    };

    return (
      <div className={classes.row} style={{ backgroundColor: bgColor }}>
        <span className={classes.lineNumber}>{lineRef.current}</span>
        <div style={{ marginLeft: `${depth * 16}px` }} className={classes.nodeContainer}>
          <span className={classes.collapseIconPlaceholder} />
          {keyName !== null && (
            <span>
              <span className={classes.tagBracket}>&quot;</span>
              <span className={classes.tagName}>{keyName}</span>
              <span className={classes.tagBracket}>&quot;</span>
              <span className={classes.jsonColon}>: </span>
            </span>
          )}
          {renderValue()}
          <span className={classes.jsonComma}>{comma}</span>
        </div>
      </div>
    );
  }

  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';

  lineRef.current++;
  const openLine = lineRef.current;

  return (
    <div>
      <div className={classes.row} style={{ backgroundColor: bgColor }}>
        <span className={classes.lineNumber}>{openLine}</span>
        <div style={{ marginLeft: `${depth * 16}px` }} className={classes.nodeContainer}>
          <span onClick={() => onToggle(id)} className={classes.collapseIcon}>
            {isCollapsed ? '▶' : '▼'}
          </span>
          {keyName !== null && (
            <span>
              <span className={classes.tagBracket}>&quot;</span>
              <span className={classes.tagName}>{keyName}</span>
              <span className={classes.tagBracket}>&quot;</span>
              <span className={classes.jsonColon}>: </span>
            </span>
          )}
          <span className={classes.jsonBracket}>{openBracket}</span>
          {isCollapsed && (
            <span className={classes.collapsedBadge} onClick={() => onToggle(id)}>
              {isArray ? `${entries.length} items` : `${entries.length} keys`}
            </span>
          )}
          {isCollapsed && (
            <span>
              <span className={classes.jsonBracket}>{closeBracket}</span>
              <span className={classes.jsonComma}>{comma}</span>
            </span>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <>
          {entries.map(([k, v], idx) => (
            <JSONNode
              key={k}
              keyName={isArray ? null : k}
              value={v}
              depth={depth + 1}
              collapsed={collapsed}
              onToggle={onToggle}
              classes={classes}
              lineRef={lineRef}
              isLast={idx === entries.length - 1}
            />
          ))}
          {(() => {
            lineRef.current++;
            return (
              <div className={classes.row} style={{ backgroundColor: bgColor }}>
                <span className={classes.lineNumber}>{lineRef.current}</span>
                <div style={{ marginLeft: `${depth * 16}px` }} className={classes.nodeContainer}>
                  <span className={classes.collapseIconPlaceholder} />
                  <span className={classes.jsonBracket}>{closeBracket}</span>
                  <span className={classes.jsonComma}>{comma}</span>
                </div>
              </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

JSONNode.propTypes = {
  keyName: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  depth: PropTypes.number,
  // eslint-disable-next-line react/forbid-prop-types
  collapsed: PropTypes.object,
  onToggle: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  lineRef: PropTypes.object,
  isLast: PropTypes.bool,
};

JSONNode.defaultProps = {
  keyName: null,
  value: null,
  depth: 0,
  collapsed: {},
  onToggle: () => {},
  classes: {},
  lineRef: { current: 0 },
  isLast: true,
};

export default JSONNode;
